import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Client, Opportunity, TeamMemberProfile } from "@salesboard/contracts";
import { api } from "@/lib/api";

/**
 * Warm a detail's data before the reader asks for it.
 *
 * **Why this exists.** Opening a detail used to show its loading skeleton for a frame or two
 * and then swap in the record — a flicker of "rendering activity" between the click and the
 * finished modal. The stale-previous-record flash was a different bug, fixed by turning off
 * the router's transitions; this is the remaining one, and it cannot be fixed by rendering
 * differently, because at click time the data genuinely is not there yet.
 *
 * So it is fetched earlier. A pointer rests on a card for tens of milliseconds before the
 * click lands, and focus reaches a row before Enter does — enough, on a local network, for the
 * request to finish. The modal then mounts with the record already in the cache and renders
 * its finished state on the first frame.
 *
 * It is a hint, never a requirement: a keyboard user who never hovers, or a cold cache on a
 * slow link, still gets the loading state, which is why that state stays. Nothing waits on
 * this promise and a failure is ignored — the modal's own query will report it properly.
 *
 * The keys and fetchers **must** match the hooks the modals use (`useOpportunityBySlug`,
 * `useClientBySlug`, `useTeamMember`), or this warms a cache entry nobody reads. They are
 * spelled out here rather than imported from three feature modules to keep this seam free of
 * feature dependencies; the test asserts they still line up.
 */
export function usePrefetchDetail(): (to: string, recordId?: string) => void {
  const queryClient = useQueryClient();

  return useCallback(
    (to: string, recordId?: string) => {
      const [, kind, slug] = to.split("/");
      if (!slug) return;
      const decoded = decodeURIComponent(slug);

      const prefetch = (queryKey: unknown[], queryFn: () => Promise<unknown>): void => {
        // `prefetchQuery` is a no-op when the entry is already fresh, so hovering a row twice
        // costs one request, not two.
        void queryClient.prefetchQuery({ queryKey, queryFn }).catch(() => undefined);
      };

      if (kind === "opportunity") {
        prefetch(["opportunities", "by-slug", decoded], () =>
          api.get<Opportunity>(`/opportunities/by-slug/${encodeURIComponent(decoded)}`),
        );
        /**
         * The **history** too, when the caller knows the record's id.
         *
         * The modal runs two queries, and warming only the first still left the second one
         * swapping a skeleton for content after the dialog was on screen — one of the three
         * heights measured in the layout-shift investigation. A board card has the id in hand,
         * so both can be warm before the click; the key mirrors `useOpportunityHistory`'s
         * default (no limit).
         */
        if (recordId) {
          prefetch(["opportunities", recordId, "history", "default"], () =>
            api.get<unknown>(`/opportunities/${recordId}/history`),
          );
        }
        return;
      }

      if (kind === "client") {
        prefetch(["clients", "by-slug", decoded], () =>
          api.get<Client>(`/clients/by-slug/${encodeURIComponent(decoded)}`),
        );
        return;
      }

      if (kind === "user") {
        prefetch(["team-member", decoded], () =>
          api.get<TeamMemberProfile>(`/users/by-slug/${encodeURIComponent(decoded)}`),
        );
      }
    },
    [queryClient],
  );
}
